import { Suspense, useEffect, useState, useRef } from "react";
import { z } from "zod";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import RootUserLayout from "@/features-root-user/layout";
import {
  organizationQueryOptions,
  useUpdateOrganization,
  useLogoPresignedUrl, // Import presigned URL hook
} from "@/features-root-user/service/organization";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import { StatusSelect } from "@/components/status-select";
import { OrganizationStatus } from "../../../../../../packages/db/src/generated";
import { settingsSchema } from "../../../../../../packages/db/src/types/organizationJsonTypes";
import { googleIntegrationSchema } from "../../../../../../packages/db/src/types/organizationJsonTypes";
import { softLedgerSchema } from "../../../../../../packages/db/src/types/organizationJsonTypes";
import { solarNetworkCredentialSchema } from "../../../../../../packages/db/src/types/organizationJsonTypes";
import { LoadingButton } from "@/components/ui/loading-button";
import { TOP_NAV } from "../constants/top-nav";
import { EnableSharing } from "../../../../../../packages/db/src/types/organizationJsonTypes";
import { UploadCloud, X } from "lucide-react"; // Icons for logo upload
import { cn } from "@/lib/utils";

const dataSharingSchema = z.object({
  enableSharing: z
    .nativeEnum(EnableSharing)
    .default(EnableSharing.DoNotShareMyData),
  shareIfMonetizable: z.boolean().default(false),
});

export const formSchema = z.object({
  name: z.string().min(1, { message: "Organization name is required." }),
  slug: z
    .string()
    .min(1, { message: "Slug is required." })
    .regex(
      /^[a-z0-9]+(-[a-z0-9]+)*[a-z0-9]+$/,
      "Invalid slug format. Use lowercase letters, numbers, and hyphens (no consecutive hyphens, no start/end with hyphen).",
    ),
  domain: z.string().min(1, { message: "Domain is required." }),
  responsiblePerson: z
    .string()
    .min(1, { message: "Responsible person is required." }),
  status: z.nativeEnum(OrganizationStatus),
  settings: settingsSchema,
  googleIntegration: googleIntegrationSchema.extend({
    useEcosuite: z.boolean().default(false),
  }),
  softLedger: softLedgerSchema,
  solarNetworkCredentials: solarNetworkCredentialSchema,
  dataSharing: dataSharingSchema,
  // Add logo file field for update form
  logoFile: z.instanceof(File).optional().nullable(),
});

type OrganizationDetailFormData = z.infer<typeof formSchema>;

// --- Main Content Component ---
function OrganizationDetailContent() {
  const { id } = useParams({
    from: "/_root/root-user/organization/organization/$id/update",
  });
  const { data: organization } = useSuspenseQuery(organizationQueryOptions(id));
  const updateMutation = useUpdateOrganization();
  const presignedUrlMutation = useLogoPresignedUrl();
  const [logoPreview, setLogoPreview] = useState<string | null>(
    organization.settings.logoUrl,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<OrganizationDetailFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: organization.name,
      slug: organization.slug,
      domain: organization.domain,
      responsiblePerson: organization.responsiblePerson,
      status: organization.status,
      settings: {
        ...organization.settings, // Spread existing settings
        logoUrl: organization.settings.logoUrl, // Ensure logoUrl is included
      },
      googleIntegration: {
        checklistTemplateId:
          organization.googleIntegration?.checklistTemplateId || "",
        googleDriveClientEmail:
          organization.googleIntegration?.googleDriveClientEmail || "",
        googleDrivePrivateKey:
          organization.googleIntegration?.googleDrivePrivateKey || "",
        projectMilestoneDatesId:
          organization.googleIntegration?.projectMilestoneDatesId || "",
        serviceRequestTemplateId:
          organization.googleIntegration?.serviceRequestTemplateId || "",
        useEcosuite: organization.googleIntegration?.useEcosuite || false,
      },
      softLedger: {
        clientId: organization.softLedger?.clientId || "",
        clientSecret: organization.softLedger?.clientSecret || "",
        tenantUUID: organization.softLedger?.tenantUUID || "",
      },
      solarNetworkCredentials: organization.solarNetworkCredentials || [],
      dataSharing: {
        ...organization.dataSharing,
        enableSharing:
          organization.dataSharing?.enableSharing ||
          EnableSharing.DoNotShareMyData,
        shareIfMonetizable:
          organization.dataSharing?.shareIfMonetizable || false,
      },
      logoFile: null, // Initialize file as null
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "solarNetworkCredentials",
  });

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      form.setValue("logoFile", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // Don't nullify if no file is chosen, keep existing preview if any
      // form.setValue("logoFile", null);
      // setLogoPreview(organization.settings.logoUrl); // Revert to original if selection is cancelled
    }
  };

  const removeLogo = () => {
    form.setValue("logoFile", null);
    form.setValue("settings.logoUrl", ""); // Also clear the stored URL
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadLogo = async (
    orgId: string,
    logoFile: File,
  ): Promise<string | null> => {
    try {
      const presignedData = await presignedUrlMutation.mutateAsync({
        orgId,
        filename: logoFile.name,
        contentType: logoFile.type,
      });

      const uploadResponse = await fetch(presignedData.uploadUrl, {
        method: "PUT",
        body: logoFile,
        headers: {
          "Content-Type": logoFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Logo upload failed: ${uploadResponse.statusText}`);
      }
      return presignedData.accessUrl;
    } catch (error) {
      toast.error("Logo upload failed.", { description: String(error) });
      return null;
    }
  };

  const onSubmit = async (data: OrganizationDetailFormData) => {
    let finalData = { ...data }; // Copy data to potentially modify logoUrl
    const { logoFile } = data;

    // If a new logo file was selected, upload it and update the logoUrl
    if (logoFile) {
      toast.info("Uploading new logo...");
      const newLogoUrl = await uploadLogo(id, logoFile);
      if (newLogoUrl) {
        // Update the logoUrl in the data to be submitted
        finalData = {
          ...finalData,
          settings: {
            ...finalData.settings,
            logoUrl: newLogoUrl,
          },
        };
        toast.success("Logo uploaded successfully!");
      } else {
        // If upload failed, stop the submission process
        toast.error("Logo upload failed. Organization not updated.");
        return;
      }
    }

    // Remove the temporary logoFile field before submitting
    const { logoFile: _, ...submitData } = finalData;

    try {
      await updateMutation.mutateAsync({ id, data: submitData });
      toast.success("Organization updated successfully!");
    } catch (error) {
      // Error handled by the mutation hook's onError
    }
  };

  // Reset form if organization data changes
  useEffect(() => {
    form.reset({
      name: organization.name,
      slug: organization.slug,
      domain: organization.domain,
      responsiblePerson: organization.responsiblePerson,
      status: organization.status,
      settings: {
        ...organization.settings,
        logoUrl: organization.settings.logoUrl,
      },
      googleIntegration: {
        checklistTemplateId:
          organization.googleIntegration?.checklistTemplateId || "",
        googleDriveClientEmail:
          organization.googleIntegration?.googleDriveClientEmail || "",
        googleDrivePrivateKey:
          organization.googleIntegration?.googleDrivePrivateKey || "",
        projectMilestoneDatesId:
          organization.googleIntegration?.projectMilestoneDatesId || "",
        serviceRequestTemplateId:
          organization.googleIntegration?.serviceRequestTemplateId || "",
        useEcosuite: organization.googleIntegration?.useEcosuite || false,
      },
      softLedger: {
        clientId: organization.softLedger?.clientId || "",
        clientSecret: organization.softLedger?.clientSecret || "",
        tenantUUID: organization.softLedger?.tenantUUID || "",
      },
      solarNetworkCredentials: organization.solarNetworkCredentials || [],
      dataSharing: {
        ...organization.dataSharing,
        enableSharing:
          organization.dataSharing?.enableSharing ||
          EnableSharing.DoNotShareMyData,
        shareIfMonetizable:
          organization.dataSharing?.shareIfMonetizable || false,
      },
      logoFile: null, // Reset file input state
    });
    setLogoPreview(organization.settings.logoUrl); // Update preview
  }, [organization, form]);

  return (
    <div className="mx-auto max-w-7xl space-y-8 p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Information */}
          <div className="space-y-6">
            <div className="border-b pb-2">
              <h2 className="text-2xl font-semibold">Basic Information</h2>
              <p className="text-muted-foreground text-sm">
                Manage the core details of your organization.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Organization Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug *</FormLabel>
                    <FormControl>
                      <Input placeholder="organization-slug" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="domain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Domain *</FormLabel>
                    <FormControl>
                      <Input placeholder="example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="responsiblePerson"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Responsible Person *</FormLabel>
                    <FormControl>
                      <Input placeholder="Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <FormControl>
                      <StatusSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select status"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          {/* Optional Fields Tabs */}
          <div className="space-y-6">
            <div className="border-b pb-2">
              <h2 className="text-2xl font-semibold">Additional Settings</h2>
              <p className="text-muted-foreground text-sm">
                Configure additional organization settings and integrations.
              </p>
            </div>
            <Tabs defaultValue="settings" className="space-y-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="google">Google Integration</TabsTrigger>
                <TabsTrigger value="softledger">SoftLedger</TabsTrigger>
                <TabsTrigger value="solar">Solar Network</TabsTrigger>
                <TabsTrigger value="datasharing">Data Sharing</TabsTrigger>
              </TabsList>
              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-6">
                {/* Logo Upload Field */}
                <FormItem>
                  <FormLabel>Organization Logo</FormLabel>
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        "flex h-24 w-24 items-center justify-center rounded-md border-2 border-dashed",
                        logoPreview ? "border-transparent" : "border-muted",
                      )}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-full w-full rounded-md object-contain"
                        />
                      ) : (
                        <UploadCloud className="text-muted-foreground h-8 w-8" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <FormControl>
                        <Input
                          type="file"
                          accept="image/png, image/jpeg, image/gif, image/svg+xml"
                          onChange={handleLogoChange}
                          className="hidden"
                          id="logo-update-upload"
                          ref={fileInputRef}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("logo-update-upload")?.click()
                        }
                      >
                        {logoPreview ? "Change Logo" : "Upload Logo"}
                      </Button>
                      {logoPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={removeLogo}
                        >
                          <X className="mr-1 h-4 w-4" /> Remove
                        </Button>
                      )}
                      <p className="text-muted-foreground text-xs">
                        PNG, JPG, GIF, SVG up to 2MB.
                      </p>
                    </div>
                  </div>
                  <FormMessage>
                    {form.formState.errors.logoFile?.message}
                  </FormMessage>
                </FormItem>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Other settings fields... */}
                  <FormField
                    control={form.control}
                    name="settings.isPublic"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium leading-none">
                            Is Public
                          </FormLabel>
                          <p className="text-muted-foreground text-sm">
                            Make this organization visible to the public
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                  {/* Hidden field for existing logo URL - needed if no new file is uploaded */}
                  <input type="hidden" {...form.register("settings.logoUrl")} />
                  <FormField
                    control={form.control}
                    name="settings.luckyOrangeSiteId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lucky Orange Site ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Site ID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="settings.projectMilestoneRecipients"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Milestone Recipients</FormLabel>
                        <FormControl>
                          {/* Ensure value is string or handle array appropriately */}
                          <Input
                            placeholder="email1@example.com, email2@example.com"
                            {...field}
                            value={
                              Array.isArray(field.value)
                                ? field.value.join(", ")
                                : (field.value ?? "")
                            }
                            onChange={(e) =>
                              field.onChange(
                                e.target.value.split(",").map((s) => s.trim()),
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="settings.qgisUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>QGIS URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://qgis.example.com"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="settings.salesforceUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Salesforce URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://salesforce.example.com"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="settings.slackXbobToken"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Slack Xbob Token</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Slack token"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
              {/* Google Tab */}
              <TabsContent value="google" className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="googleIntegration.useEcosuite"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium leading-none">
                            Use Ecosuite
                          </FormLabel>
                          <p className="text-muted-foreground text-sm">
                            Enable Ecosuite integration with Google
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="googleIntegration.checklistTemplateId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Checklist Template ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Template ID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="googleIntegration.googleDriveClientEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Drive Client Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="client@example.com"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="googleIntegration.googleDrivePrivateKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Drive Private Key</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Private Key"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="googleIntegration.projectMilestoneDatesId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Milestone Dates ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Milestone Dates ID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="googleIntegration.serviceRequestTemplateId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Service Request Template ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Service Request Template ID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
              {/* SoftLedger Tab */}
              <TabsContent value="softledger" className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="softLedger.clientId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="SoftLedger Client ID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="softLedger.clientSecret"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Secret</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="SoftLedger Client Secret"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="softLedger.tenantUUID"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tenant UUID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Tenant UUID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
              {/* Solar Network Tab */}
              <TabsContent value="solar" className="space-y-6">
                <div className="space-y-6">
                  {fields.map((item, index) => (
                    <div
                      key={item.id}
                      className="rounded-lg border p-6 shadow-sm"
                    >
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <FormField
                          control={form.control}
                          name={`solarNetworkCredentials.${index}.alias`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Alias</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Credential Alias"
                                  {...field}
                                  value={field.value ?? ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`solarNetworkCredentials.${index}.host`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Host</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Host"
                                  {...field}
                                  value={field.value ?? ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`solarNetworkCredentials.${index}.secret`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Secret</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Secret"
                                  {...field}
                                  value={field.value ?? ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`solarNetworkCredentials.${index}.sshHost`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>SSH Host</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="SSH Host"
                                  {...field}
                                  value={field.value ?? ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`solarNetworkCredentials.${index}.token`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Token</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Token"
                                  {...field}
                                  value={field.value ?? ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`solarNetworkCredentials.${index}.wssHost`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>WSS Host</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="WSS Host"
                                  {...field}
                                  value={field.value ?? ""}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="mt-4">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => remove(index)}
                        >
                          Remove Credential
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    onClick={() => {
                      append({
                        alias: "",
                        host: "",
                        secret: "",
                        sshHost: "",
                        token: "",
                        wssHost: "",
                      });
                      form.clearErrors();
                    }}
                  >
                    Add Credential
                  </Button>
                </div>
              </TabsContent>
              {/* Data Sharing Tab */}
              <TabsContent value="datasharing" className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="settings.isPublic" // Assuming isPublic is part of general settings but shown here for relevance
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium leading-none">
                            Public Organization
                          </FormLabel>
                          <p className="text-muted-foreground text-sm">
                            Make organization visible to the public
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dataSharing.enableSharing"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Enable Data Sharing</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={
                              field.value ?? EnableSharing.DoNotShareMyData
                            } // Ensure a default value for Select
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select data sharing option" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem
                                value={EnableSharing.DoNotShareMyData}
                              >
                                Do not share my data
                              </SelectItem>
                              <SelectItem
                                value={
                                  EnableSharing.ShareMyDataButItMustBeAnonymized
                                }
                              >
                                Share my data but it must be anonymized
                              </SelectItem>
                              <SelectItem
                                value={EnableSharing.ShareMyDataFully}
                              >
                                Share my data fully
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {form.watch("dataSharing.enableSharing") !==
                  EnableSharing.DoNotShareMyData && (
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="dataSharing.shareIfMonetizable"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value ?? false} // Handle potentially undefined value
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-sm font-medium leading-none">
                              Only share if monetization is possible
                            </FormLabel>
                            <p className="text-muted-foreground text-sm">
                              Only share data when there's a revenue opportunity
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
          {/* Submit Button */}
          <div className="mt-8 flex justify-end">
            <LoadingButton
              type="submit"
              className="w-full md:w-auto"
              isLoading={
                form.formState.isSubmitting || presignedUrlMutation.isPending
              }
              disabled={
                form.formState.isSubmitting || presignedUrlMutation.isPending
              }
            >
              Update Organization
            </LoadingButton>
          </div>
        </form>
      </Form>
    </div>
  );
}

// --- Main Export Component ---
export default function UpdateOrganization() {
  return (
    <RootUserLayout
      pageTitle="Update Organization"
      pageDescription="Update the organization details below."
      topNav={TOP_NAV}
    >
      <Suspense
        fallback={
          <LoadingScreen
            text="Loading Organization..."
            className="h-[calc(100vh-180px)]"
          />
        }
      >
        <EcosuiteErrorBoundary>
          <OrganizationDetailContent />
        </EcosuiteErrorBoundary>
      </Suspense>
    </RootUserLayout>
  );
}
